import React, { useEffect, useState } from 'react';
import { referralAPI, uploadEvidenceDirectly } from './api';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const safeError = (error, fallback) => (
  error.response?.data?.detail || fallback
);

const statusCopy = {
  submitted: {
    title: 'Submitted for manual review',
    body: 'Your evidence was submitted securely. An authorized administrator will review it.'
  },
  under_review: {
    title: 'Review in progress',
    body: 'An authorized administrator is reviewing your referral application.'
  },
  approved: {
    title: 'Referral approved',
    body: 'Your full-platform access is active.'
  },
  expired: {
    title: 'Referral expired',
    body: 'This referral application expired before completion.'
  }
};

const denialReasonCopy = {
  unable_to_verify: 'We could not verify eligibility from this referral attempt.',
  evidence_insufficient: 'The submitted document was not sufficient to approve this referral attempt.'
};

export default function ReferralAccess({ onReturnToAccessOptions = null }) {
  const [application, setApplication] = useState(null);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    referralAPI.getCurrent()
      .then((response) => {
        if (active) {
          setApplication(response.data);
          setPhase(
            ['awaiting_evidence', 'needs_more_evidence'].includes(response.data.status)
              && response.data.evidence_uploaded
              ? 'upload_success'
              : 'ready'
          );
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(safeError(requestError, 'Unable to load your referral application.'));
          setPhase('error');
        }
      });
    return () => { active = false; };
  }, []);

  const chooseFile = (event) => {
    const selected = event.target.files?.[0] || null;
    setError('');
    if (!selected) {
      setFile(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setFile(null);
      setError('Choose a PDF, JPEG, or PNG file.');
      return;
    }
    if (selected.size > MAX_FILE_BYTES) {
      setFile(null);
      setError('The evidence file must be 10 MB or smaller.');
      return;
    }
    setFile(selected);
  };

  const upload = async () => {
    if (!file || !application) return;
    setError('');
    setPhase('authorizing');
    try {
      const response = await referralAPI.authorizeUpload(
        application.referral_id,
        file.type,
        file.size
      );
      setPhase('upload_ready');
      await uploadEvidenceDirectly(response.data, file);
      setPhase('upload_success');
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to upload evidence safely.'));
      setPhase('ready');
    }
  };

  const submit = async () => {
    setError('');
    setPhase('submitting');
    try {
      const response = await referralAPI.submit(application.referral_id);
      setApplication(response.data);
      setFile(null);
      setPhase('ready');
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to confirm the referral submission.'));
      setPhase('upload_success');
    }
  };

  if (phase === 'loading') {
    return <p className="mt-6 text-center text-gray-600" role="status">Loading referral application…</p>;
  }

  if (!application) {
    return error ? <p className="mt-6 text-red-700" role="alert">{error}</p> : null;
  }

  const settled = application.status === 'denied'
    ? {
      title: 'Prior referral attempt not approved',
      body: denialReasonCopy[application.decision_reason_code]
        || 'We could not approve this referral attempt.'
    }
    : statusCopy[application.status];
  const needsMoreEvidence = application.status === 'needs_more_evidence';
  return (
    <section className="mt-10 bg-white border border-gray-200 rounded-2xl shadow-sm p-6" aria-labelledby="referral-access-title">
      <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">Referral / Free Access</p>
      <p className="mt-1 text-sm font-medium text-gray-600">
        Attempt {application.attempt_number}
      </p>
      <h2 id="referral-access-title" className="mt-2 text-2xl font-bold text-blue-950">
        {settled?.title || (needsMoreEvidence
          ? 'Another supporting document is needed'
          : 'Secure evidence submission')}
      </h2>

      {settled ? (
        <>
          <p className="mt-3 text-gray-700">{settled.body}</p>
          {application.notification_status === 'awaiting_provider' && (
            <p className="mt-3 text-sm text-amber-800">
              Your decision is recorded. Email delivery is not configured yet.
            </p>
          )}
          {application.status === 'denied' && onReturnToAccessOptions && (
            <button
              type="button"
              className="mt-4 gpj-btn gpj-btn--primary gpj-btn--sm"
              onClick={onReturnToAccessOptions}
            >
              Return to Access Options
            </button>
          )}
        </>
      ) : (
        <>
          {needsMoreEvidence && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-950">
              We need another supporting document before we can complete the review.
            </p>
          )}
          <p className="mt-3 text-gray-700">
            Submit one supporting document for manual eligibility review. Upload only what is necessary.
            GPJ accepts PDF, JPEG, or PNG files up to 10 MB.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            The document is sent directly to private evidence storage. GPJ does not extract text or send it to AI.
          </p>

          {phase !== 'upload_success' && phase !== 'submitting' && (
            <div className="mt-5">
              <label className="block text-sm font-medium text-blue-950" htmlFor="referral-evidence">
                Supporting evidence
              </label>
              <input
                id="referral-evidence"
                className="mt-2 block w-full text-sm text-gray-700"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={chooseFile}
              />
              <button
                type="button"
                className="mt-4 gpj-btn gpj-btn--primary gpj-btn--sm"
                disabled={!file || ['authorizing', 'upload_ready'].includes(phase)}
                onClick={upload}
              >
                {phase === 'authorizing'
                  ? 'Preparing secure upload…'
                  : phase === 'upload_ready'
                    ? 'Uploading directly to secure storage…'
                    : 'Authorize and upload'}
              </button>
            </div>
          )}

          {(phase === 'upload_success' || phase === 'submitting') && (
            <div className="mt-5 rounded-lg border border-green-200 bg-green-50 p-4">
              <strong className="text-green-900">Upload complete</strong>
              <p className="mt-1 text-sm text-green-900">
                Confirm submission to place the application in the manual review queue.
              </p>
              <button
                type="button"
                className="mt-3 gpj-btn gpj-btn--primary gpj-btn--sm"
                disabled={phase === 'submitting'}
                onClick={submit}
              >
                {phase === 'submitting' ? 'Submitting…' : 'Submit for review'}
              </button>
            </div>
          )}
        </>
      )}

      {error && <p className="mt-4 text-red-700" role="alert">{error}</p>}
    </section>
  );
}
